import { useRef, useState } from "react";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Clock, ImagePlus, X, Loader2, ShieldCheck } from "lucide-react";
import { Reveal, SectionHead } from "./Reveal";
import { PHONE, PHONE_HREF, EMAIL, EMAIL_HREF, LICENSE } from "./data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-navy placeholder:text-slate-400 outline-none transition-[border-color,box-shadow] focus:border-gold focus:bg-white focus:ring-2 focus:ring-gold/30";

const labelCls = "mb-1.5 block text-[11px] font-extrabold uppercase tracking-[0.14em] text-navy/70";

const SCOPES = ["Interior", "Exterior", "Both"];

const EMPTY = { name: "", phone: "", email: "", city: "", projectType: "", service: "", scope: "Interior", size: "", message: "" };

const ContactForm = () => {
  const [form, setForm] = useState(EMPTY);
  const [photos, setPhotos] = useState([]);
  const [sending, setSending] = useState(false);
  const fileRef = useRef(null);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const addPhotos = (e) => {
    const files = Array.from(e.target.files || []).slice(0, 6 - photos.length);
    setPhotos((p) => [...p, ...files]);
    e.target.value = "";
  };

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const fd = new FormData();
      fd.append("name", form.name);
      fd.append("phone", form.phone);
      fd.append("email", form.email);
      fd.append("city", form.city);
      fd.append("project_type", form.projectType);
      fd.append("service", form.service);
      fd.append("scope", form.scope);
      fd.append("size", form.size);
      fd.append("message", form.message);
      photos.forEach((p) => fd.append("photos", p));
      const res = await fetch(`${API}/estimate`, { method: "POST", body: fd });
      if (!res.ok) throw new Error("Request failed");
      toast.success("Estimate request sent! Teo's Painting will contact you shortly.");
      setForm(EMPTY);
      setPhotos([]);
    } catch {
      toast.error(`Something went wrong. Please call ${PHONE} instead.`);
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="bg-white py-24 sm:py-32">
      <div className="mx-auto grid max-w-7xl gap-14 px-5 sm:px-8 lg:grid-cols-5">
        <div className="lg:col-span-2">
          <SectionHead num="07" eyebrow="Free Estimate" title="Request Your Free Estimate" testid="contact-head" />
          <Reveal delay={0.15}>
            <p className="mt-6 text-base leading-relaxed text-slate-600">
              Tell us about your project and we'll get back to you with a free, no-pressure estimate.
              Prefer to talk? We're a phone call away.
            </p>
          </Reveal>

          <div className="mt-9 space-y-4" data-testid="contact-info-cards">
            {[
              { icon: Phone, label: "CALL OR TEXT", value: PHONE, href: PHONE_HREF, testid: "contact-phone-link" },
              { icon: Mail, label: "EMAIL", value: EMAIL, href: EMAIL_HREF, testid: "contact-email-link" },
              { icon: MapPin, label: "SERVICE AREA", value: "Reno · Sparks · Carson City, NV", testid: "contact-area" },
              { icon: Clock, label: "RESPONSE TIME", value: "Typically within 1 business day", testid: "contact-response" },
            ].map(({ icon: Icon, label, value, href, testid }) => (
              <Reveal key={label}>
                <div className="flex items-center gap-4 rounded-2xl border border-slate-200 bg-cream p-4">
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-navy text-gold">
                    <Icon size={19} strokeWidth={1.9} />
                  </span>
                  <span className="min-w-0">
                    <span className="block font-mono text-[10px] tracking-[0.22em] text-slate-400">{label}</span>
                    {href ? (
                      <a href={href} data-testid={testid} className="block truncate text-sm font-bold text-navy transition-colors hover:text-gold-muted sm:text-base">
                        {value}
                      </a>
                    ) : (
                      <span data-testid={testid} className="block text-sm font-bold text-navy sm:text-base">{value}</span>
                    )}
                  </span>
                </div>
              </Reveal>
            ))}
            <Reveal>
              <p className="flex items-center gap-2 pl-1 text-xs font-semibold text-slate-500">
                <ShieldCheck size={15} className="text-gold" /> {LICENSE} · 20+ years of experience
              </p>
            </Reveal>
          </div>
        </div>

        <Reveal delay={0.1} className="lg:col-span-3">
          <form
            data-testid="estimate-form"
            onSubmit={submit}
            className="rounded-3xl border border-slate-200 bg-white p-6 shadow-[0_30px_70px_-30px_rgba(11,25,44,0.3)] sm:p-10"
          >
            <div className="grid gap-5 sm:grid-cols-2">
              <div>
                <label htmlFor="ef-name" className={labelCls}>Name *</label>
                <input id="ef-name" data-testid="estimate-form-name" required value={form.name} onChange={set("name")} placeholder="Your full name" className={inputCls} />
              </div>
              <div>
                <label htmlFor="ef-phone" className={labelCls}>Phone *</label>
                <input id="ef-phone" data-testid="estimate-form-phone" required type="tel" value={form.phone} onChange={set("phone")} placeholder="(775) 555-0123" className={inputCls} />
              </div>
              <div>
                <label htmlFor="ef-email" className={labelCls}>Email *</label>
                <input id="ef-email" data-testid="estimate-form-email" required type="email" value={form.email} onChange={set("email")} placeholder="you@email.com" className={inputCls} />
              </div>
              <div>
                <label htmlFor="ef-city" className={labelCls}>City *</label>
                <select id="ef-city" data-testid="estimate-form-city" required value={form.city} onChange={set("city")} className={inputCls}>
                  <option value="" disabled>Select your city</option>
                  <option>Reno</option>
                  <option>Sparks</option>
                  <option>Carson City</option>
                  <option>Other Northern Nevada</option>
                </select>
              </div>
              <div>
                <label htmlFor="ef-type" className={labelCls}>Project Type *</label>
                <select id="ef-type" data-testid="estimate-form-project-type" required value={form.projectType} onChange={set("projectType")} className={inputCls}>
                  <option value="" disabled>Residential or commercial?</option>
                  <option>Residential</option>
                  <option>Commercial</option>
                </select>
              </div>
              <div>
                <label htmlFor="ef-service" className={labelCls}>Service Needed *</label>
                <select id="ef-service" data-testid="estimate-form-service" required value={form.service} onChange={set("service")} className={inputCls}>
                  <option value="" disabled>What can we paint for you?</option>
                  <option>Interior Painting</option>
                  <option>Exterior Painting</option>
                  <option>Cabinet Refinishing</option>
                  <option>Fence Staining</option>
                  <option>Deck Staining</option>
                  <option>Multiple / Not Sure Yet</option>
                </select>
              </div>
              <div className="sm:col-span-2">
                <span className={labelCls} id="ef-scope-label">Interior / Exterior</span>
                <div className="flex gap-2" role="radiogroup" aria-labelledby="ef-scope-label">
                  {SCOPES.map((s) => (
                    <button
                      key={s}
                      type="button"
                      role="radio"
                      aria-checked={form.scope === s}
                      data-testid={`estimate-form-scope-${s.toLowerCase()}`}
                      onClick={() => setForm((f) => ({ ...f, scope: s }))}
                      className={`flex-1 rounded-xl border px-4 py-3 text-sm font-bold transition-[background-color,border-color,color] duration-200 ${
                        form.scope === s
                          ? "border-navy bg-navy text-gold"
                          : "border-slate-200 bg-slate-50 text-slate-500 hover:border-gold/60"
                      }`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
              <div className="sm:col-span-2">
                <label htmlFor="ef-size" className={labelCls}>Approximate Project Size</label>
                <input id="ef-size" data-testid="estimate-form-size" value={form.size} onChange={set("size")} placeholder="e.g. 3 bedrooms, ~1,800 sq ft exterior, 20 cabinet doors" className={inputCls} />
              </div>
              <div className="sm:col-span-2">
                <label htmlFor="ef-message" className={labelCls}>Message</label>
                <textarea id="ef-message" data-testid="estimate-form-message" rows={4} value={form.message} onChange={set("message")} placeholder="Tell us about your project — colors, timeline, anything that helps." className={`${inputCls} resize-none`} />
              </div>
              <div className="sm:col-span-2">
                <span className={labelCls}>Upload Photos</span>
                <input ref={fileRef} type="file" accept="image/*" multiple hidden onChange={addPhotos} data-testid="estimate-form-photos-input" />
                <button
                  type="button"
                  data-testid="estimate-form-photos-button"
                  onClick={() => fileRef.current?.click()}
                  className="flex w-full flex-col items-center gap-2 rounded-xl border-2 border-dashed border-slate-300 bg-slate-50 px-4 py-7 text-sm font-semibold text-slate-500 transition-colors hover:border-gold hover:bg-gold/5 hover:text-navy"
                >
                  <ImagePlus size={24} className="text-gold" />
                  Tap to upload photos of your project <span className="text-xs font-normal text-slate-400">optional · up to 6 photos</span>
                </button>
                {photos.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-3" data-testid="estimate-form-photos-preview">
                    {photos.map((p, i) => (
                      <span key={i} className="relative">
                        <img src={URL.createObjectURL(p)} alt={`Upload preview ${i + 1}`} className="h-16 w-16 rounded-lg border border-slate-200 object-cover" />
                        <button
                          type="button"
                          data-testid={`estimate-form-photo-remove-${i}`}
                          aria-label={`Remove photo ${i + 1}`}
                          onClick={() => setPhotos((arr) => arr.filter((_, j) => j !== i))}
                          className="absolute -right-2 -top-2 flex h-5 w-5 items-center justify-center rounded-full bg-navy text-white shadow"
                        >
                          <X size={11} />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <button
              type="submit"
              data-testid="estimate-form-submit-button"
              disabled={sending}
              className="mt-7 flex w-full items-center justify-center gap-2.5 rounded-full bg-gold px-8 py-4 text-[13px] font-extrabold tracking-[0.16em] text-navy shadow-[0_18px_40px_-14px_rgba(212,175,55,0.6)] transition-[background-color,transform,opacity] duration-200 hover:scale-[1.02] hover:bg-gold-light disabled:cursor-not-allowed disabled:opacity-60"
            >
              {sending && <Loader2 size={17} className="animate-spin" />}
              {sending ? "SENDING YOUR REQUEST…" : "REQUEST MY FREE ESTIMATE"}
            </button>
            <p className="mt-4 text-center text-xs text-slate-400">
              No spam, no obligation — just a straightforward estimate from a local pro.
            </p>
          </form>
        </Reveal>
      </div>
    </section>
  );
};

export default ContactForm;
